import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';


const AURELIA_COLOUR_LIBRARY = Object.freeze({
  black: [18, 18, 18],
  charcoal: [58, 62, 66],
  graphite: [71, 75, 78],
  grey: [128, 128, 128],
  silver: [192, 192, 192],
  white: [248, 248, 246],
  offwhite: [241, 238, 227],
  ivory: [246, 240, 218],
  cream: [244, 231, 205],
  ecru: [225, 211, 183],
  beige: [213, 195, 163],
  sand: [203, 181, 145],
  nude: [216, 180, 156],
  tan: [193, 154, 107],
  camel: [184, 134, 88],
  taupe: [139, 125, 114],
  khaki: [181, 171, 112],
  brown: [111, 78, 55],
  chocolate: [76, 46, 36],
  mocha: [126, 92, 73],
  coffee: [93, 62, 43],
  red: [198, 40, 40],
  burgundy: [123, 30, 43],
  maroon: [112, 31, 45],
  wine: [119, 37, 58],
  coral: [242, 112, 98],
  orange: [230, 126, 34],
  rust: [181, 82, 57],
  terracotta: [190, 99, 72],
  peach: [246, 179, 142],
  yellow: [244, 196, 48],
  mustard: [196, 154, 0],
  gold: [212, 175, 55],
  green: [46, 125, 50],
  forest: [34, 85, 52],
  emerald: [34, 139, 94],
  olive: [107, 125, 42],
  sage: [156, 175, 136],
  mint: [152, 216, 176],
  teal: [42, 140, 130],
  aqua: [64, 192, 192],
  turquoise: [64, 190, 184],
  blue: [47, 111, 178],
  navy: [27, 42, 73],
  midnight: [25, 35, 62],
  royal: [47, 82, 181],
  cobalt: [0, 71, 171],
  sky: [135, 206, 235],
  denim: [79, 109, 138],
  periwinkle: [140, 146, 210],
  purple: [126, 87, 194],
  violet: [117, 70, 166],
  lavender: [179, 157, 219],
  lilac: [198, 166, 220],
  plum: [111, 44, 95],
  pink: [232, 139, 178],
  blush: [232, 180, 184],
  rose: [217, 108, 138],
  fuchsia: [216, 27, 96],
  magenta: [202, 31, 123],
  bronze: [176, 141, 87],
  copper: [184, 115, 51],
});

const AURELIA_COLOUR_ALIASES = Object.freeze({
  gray: 'grey',
  greige: 'taupe',
  anthracite: 'charcoal',
  slate: 'graphite',
  ash: 'grey',
  natural: 'beige',
  oatmeal: 'beige',
  stone: 'taupe',
  mushroom: 'taupe',
  champagne: 'cream',
  vanilla: 'cream',
  linen: 'ecru',
  biscuit: 'beige',
  caramel: 'camel',
  cognac: 'tan',
  chestnut: 'brown',
  espresso: 'coffee',
  cocoa: 'chocolate',
  crimson: 'red',
  scarlet: 'red',
  cherry: 'red',
  berry: 'burgundy',
  merlot: 'wine',
  oxblood: 'burgundy',
  brick: 'rust',
  tangerine: 'orange',
  lemon: 'yellow',
  sunshine: 'yellow',
  moss: 'olive',
  army: 'olive',
  military: 'olive',
  pine: 'forest',
  bottle: 'forest',
  jade: 'emerald',
  seafoam: 'mint',
  sea: 'aqua',
  ocean: 'aqua',
  marine: 'navy',
  indigo: 'navy',
  ink: 'navy',
  azure: 'blue',
  powder: 'sky',
  babyblue: 'sky',
  cornflower: 'periwinkle',
  mauve: 'purple',
  orchid: 'purple',
  grape: 'purple',
  aubergine: 'plum',
  eggplant: 'plum',
  raspberry: 'fuchsia',
  hotpink: 'fuchsia',
  dustyrose: 'rose',
  rosegold: 'rose',
});

const AURELIA_COLOUR_PHRASES = Object.freeze([
  ['off white', 'offwhite'],
  ['warm white', 'offwhite'],
  ['navy blue', 'navy'],
  ['midnight blue', 'midnight'],
  ['royal blue', 'royal'],
  ['cobalt blue', 'cobalt'],
  ['sky blue', 'sky'],
  ['powder blue', 'sky'],
  ['baby blue', 'sky'],
  ['denim blue', 'denim'],
  ['light blue', 'sky'],
  ['pale blue', 'sky'],
  ['dark blue', 'navy'],
  ['light pink', 'blush'],
  ['pale pink', 'blush'],
  ['light green', 'mint'],
  ['pale green', 'mint'],
  ['dark green', 'forest'],
  ['light purple', 'lavender'],
  ['pale purple', 'lavender'],
  ['dark purple', 'plum'],
  ['dark red', 'burgundy'],
  ['dark brown', 'chocolate'],
  ['light beige', 'cream'],
  ['forest green', 'forest'],
  ['emerald green', 'emerald'],
  ['olive green', 'olive'],
  ['sage green', 'sage'],
  ['mint green', 'mint'],
  ['bottle green', 'forest'],
  ['hot pink', 'fuchsia'],
  ['dusty rose', 'rose'],
  ['rose gold', 'rose'],
  ['wine red', 'wine'],
  ['brick red', 'rust'],
  ['burnt orange', 'rust'],
  ['light grey', 'grey'],
  ['dark grey', 'charcoal'],
]);

const AURELIA_PATTERN_WORDS = new Set([
  'animal', 'check', 'checked', 'dot', 'dots', 'floral', 'flower', 'flowers', 'leopard',
  'multi', 'multicolour', 'multicolor', 'ombre', 'paisley', 'pattern', 'print', 'printed',
  'stripe', 'striped', 'stripes', 'two tone', 'twotone',
]);

class AureliaBundleBuyBox extends HTMLElement {
  #variants = [];
  #setVariants = [];
  #abortController = null;
  #syncTimer = null;
  #successTimer = null;
  #initialized = false;
  #isLoading = false;
  #secondBundleItemCustomized = false;
  #bundleConfigured = false;
  #singleConfigured = false;
  #lastOfferQuantity = 2;
  #buttonObserver = null;
  #componentObserver = null;
  #overlayObserver = null;
  #componentHasEnteredViewport = false;
  #buttonHasEnteredViewport = false;
  #stickyBar = null;
  #productInformationSection = null;
  #stickyOriginalParent = null;
  #stickyOriginalNextSibling = null;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#variants = this.#readJson('[data-main-variants]');
    this.#setVariants = this.#readJson('[data-set-variants]');
    this.#lastOfferQuantity = this.#getOfferQuantity();

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('scroll', this.#updateStickyVisibility, { passive: true });
    window.addEventListener('resize', this.#updateStickyVisibility, { passive: true });

    this.#secondBundleItemCustomized = this.#isCompleteSet() ? true : !this.#areItemsEqual(2, 3);
    if (this.#isCompleteSet()) this.#matchSetColourToMain();
    this.#setupStickyBar();
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('scroll', this.#updateStickyVisibility);
    window.removeEventListener('resize', this.#updateStickyVisibility);
    this.#buttonObserver?.disconnect();
    this.#componentObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#stickyBar?.removeEventListener('click', this.#handleClick);

    if (this.#stickyBar && this.#stickyOriginalParent?.isConnected) {
      this.#stickyOriginalParent.insertBefore(
        this.#stickyBar,
        this.#stickyOriginalNextSibling?.parentNode === this.#stickyOriginalParent
          ? this.#stickyOriginalNextSibling
          : null
      );
    }

    this.#stickyBar = null;
    this.#productInformationSection = null;
    this.#stickyOriginalParent = null;
    this.#stickyOriginalNextSibling = null;
    this.#abortController?.abort();
    if (this.#syncTimer) window.clearTimeout(this.#syncTimer);
    if (this.#successTimer) window.clearTimeout(this.#successTimer);
    this.#initialized = false;
  }

  #readJson(selector) {
    const script = this.querySelector(selector);
    if (!script?.textContent) return [];

    try {
      const value = JSON.parse(script.textContent);
      return Array.isArray(value) ? value : value ? [value] : [];
    } catch (error) {
      console.error(`[Aurelia bundle] Invalid JSON in ${selector}`, error);
      return [];
    }
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-bundle-submit], [data-sticky-submit]')) {
      event.preventDefault();
      this.#handleSubmit();
      return;
    }

    if (target.closest('[data-copy-item-one]')) {
      event.preventDefault();
      this.#copySelections(2, 3);
      this.#secondBundleItemCustomized = false;
      this.#clearMessage();
      this.#clearValidationErrors();
      this.#update();
      return;
    }

    const sizeGuideOpen = target.closest('[data-size-guide-open]');
    if (sizeGuideOpen) {
      event.preventDefault();

      const dialogId = sizeGuideOpen.getAttribute('aria-controls');
      const dialogComponent = dialogId ? document.getElementById(dialogId) : null;

      // The offer contains two different products in complete-set mode. Send
      // the context belonging to the clicked row to the shared Size Guide so
      // a shorts link opens bottoms guidance and a sandals link opens footwear
      // guidance instead of always using the main product.
      const itemContextNode = sizeGuideOpen
        .closest('[data-bundle-item]')
        ?.querySelector('[data-size-guide-item-context]');

      if (itemContextNode && dialogComponent) {
        try {
          const itemContext = JSON.parse(itemContextNode.textContent || '{}');
          dialogComponent
            .querySelector('[data-asg-dialog-content]')
            ?.dispatchEvent(
              new CustomEvent('aurelia:size-guide-context', {
                detail: itemContext,
              })
            );
        } catch (error) {
          console.warn('[Aurelia bundle] Size guide product context could not be read.', error);
        }
      }

      const openSizeGuide = () => {
        if (!dialogComponent) return;

        // Horizon places the ID on <dialog-component>, not on the inner
        // <dialog>. Use the component API so scroll locking, focus handling,
        // outside-click closing and Escape all behave exactly like the working
        // Size guide link in the single-item card.
        if (typeof dialogComponent.showDialog === 'function') {
          dialogComponent.showDialog();
          return;
        }

        // Safe fallback in case the custom element has not upgraded yet.
        const dialog =
          dialogComponent instanceof HTMLDialogElement
            ? dialogComponent
            : dialogComponent.querySelector('dialog');

        if (dialog instanceof HTMLDialogElement && !dialog.open) {
          dialog.showModal();
        }
      };

      if (customElements.get('dialog-component')) {
        openSizeGuide();
      } else {
        customElements.whenDefined('dialog-component').then(openSizeGuide);
      }

      return;
    }

    if (target.closest('[data-size-guide-close]')) {
      event.preventDefault();
      const dialog = target.closest('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.close();
      return;
    }

    if (target.matches('[data-size-guide-dialog]')) {
      const dialog = target;
      if (dialog instanceof HTMLDialogElement) dialog.close();
    }
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    this.#clearMessage();
    this.#clearValidationErrors();

    if (target.matches('.as-bundle__offer-input')) {
      const quantity = this.#getOfferQuantity();

      if (quantity === 2 && this.#lastOfferQuantity === 1 && !this.#bundleConfigured) {
        this.#copySelections(1, 2);
        if (!this.#isCompleteSet()) {
          this.#copySelections(2, 3);
          this.#secondBundleItemCustomized = false;
        } else {
          this.#matchSetColourToMain();
        }
      }

      if (quantity === 1 && this.#lastOfferQuantity === 2 && !this.#singleConfigured) {
        this.#copySelections(2, 1);
      }

      this.#lastOfferQuantity = quantity;
      this.#update();
      this.#schedulePrimaryVariantSync();
      return;
    }

    if (target.matches('[data-master-option]')) {
      this.#applyMasterOption(target);
      if (this.#isCompleteSet()) this.#matchSetColourToMain();
      this.#bundleConfigured = true;
      this.#singleConfigured = true;
      this.#secondBundleItemCustomized = this.#isCompleteSet();
      this.#update();
      this.#schedulePrimaryVariantSync();
      return;
    }

    if (target.matches('[data-bundle-option]')) {
      const itemIndex = Number(target.dataset.itemIndex || 0);
      const optionIndex = Number(target.dataset.optionIndex || 0);
      this.#ensureValidCombination(itemIndex, optionIndex, target.value);

      if (this.#isCompleteSet() && target.dataset.optionType === 'colour') {
        if (itemIndex === 1) {
          const bundleColour = this.#getOptionControls(2).find((control) => control.dataset.optionType === 'colour');
          if (bundleColour instanceof HTMLSelectElement) {
            bundleColour.value = target.value;
            this.#ensureValidCombination(2, Number(bundleColour.dataset.optionIndex || 0), target.value);
          }
        }
        if (itemIndex === 1 || itemIndex === 2) this.#matchSetColourToMain();
      }

      if (itemIndex === 1) this.#singleConfigured = true;
      if (itemIndex === 2 || itemIndex === 3) this.#bundleConfigured = true;

      if (itemIndex === 2 && !this.#secondBundleItemCustomized) {
        this.#copySelections(2, 3);
      }

      if (itemIndex === 3) {
        this.#secondBundleItemCustomized = this.#isCompleteSet() ? true : !this.#areItemsEqual(2, 3);
      }

      this.#update();
      if (itemIndex === this.#getPrimaryItemIndex()) this.#schedulePrimaryVariantSync();
      return;
    }

  };

  #schedulePrimaryVariantSync() {
    if (this.#syncTimer) window.clearTimeout(this.#syncTimer);
    this.#syncTimer = window.setTimeout(() => this.#syncPrimaryVariant(), 100);
  }

  #getOfferQuantity() {
    const selected = this.querySelector('.as-bundle__offer-input:checked');
    return selected instanceof HTMLInputElement ? Number(selected.value) : Number(this.dataset.defaultOffer || 2);
  }

  #getPrimaryItemIndex() {
    return this.#getOfferQuantity() === 2 ? 2 : 1;
  }

  #getActiveItemIndices() {
    return this.#getOfferQuantity() === 2 ? [2, 3] : [1];
  }

  #isCompleteSet() {
    return this.dataset.bundleMode === 'complete-set' && this.#setVariants.length > 0;
  }

  #variantsForItem(itemIndex) {
    return this.#isCompleteSet() && Number(itemIndex) === 3 ? this.#setVariants : this.#variants;
  }

  #productTitleForItem(itemIndex) {
    if (this.#isCompleteSet() && Number(itemIndex) === 3) {
      return this.dataset.secondProductTitle || 'matching product';
    }
    return this.dataset.productTitle || 'product';
  }

  #getOptionControls(itemIndex) {
    return Array.from(this.querySelectorAll(`[data-bundle-option][data-item-index="${itemIndex}"]`))
      .filter((control) => control instanceof HTMLSelectElement)
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
  }

  #getSelectedOptions(itemIndex) {
    return this.#getOptionControls(itemIndex).map((control) => control.value || null);
  }

  #getSelectedDisplayOptions(itemIndex) {
    return this.#getOptionControls(itemIndex).map((control) => {
      const selectedOption = control.selectedOptions[0];
      return selectedOption?.dataset.displayValue || control.value || null;
    });
  }

  #getSelectedOptionDetails(itemIndex) {
    return this.#getOptionControls(itemIndex).map((control) => ({
      name: control.dataset.optionName || '',
      value: control.value,
      valueId: control.selectedOptions[0]?.dataset.optionValueId || '',
    }));
  }

  #findVariant(itemIndex) {
    const variants = this.#variantsForItem(itemIndex);
    if (!variants.length) return null;
    const controls = this.#getOptionControls(itemIndex);
    if (!controls.length) return variants[0] || null;

    const options = this.#getSelectedOptions(itemIndex);
    return variants.find(
      (variant) => Array.isArray(variant.options) &&
        variant.options.length === options.length &&
        variant.options.every((value, index) => value === options[index])
    ) || null;
  }

  #findDisplayVariant(itemIndex) {
    const variants = this.#variantsForItem(itemIndex);
    const exact = this.#findVariant(itemIndex);
    if (exact) return exact;

    const selectedOptions = this.#getSelectedOptions(itemIndex);
    const candidates = variants.filter((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return selectedOptions.every((value, index) => !value || variant.options[index] === value);
    });

    return candidates.find((variant) => variant.available) || candidates[0] || variants.find((variant) => variant.available) || variants[0] || null;
  }

  #copySelections(sourceIndex, targetIndex) {
    const sourceControls = this.#getOptionControls(sourceIndex);
    const targetControls = this.#getOptionControls(targetIndex);

    for (const source of sourceControls) {
      const target = targetControls.find(
        (control) => Number(control.dataset.optionIndex) === Number(source.dataset.optionIndex)
      );
      if (target instanceof HTMLSelectElement) target.value = source.value;
    }
  }

  #areItemsEqual(firstIndex, secondIndex) {
    const first = this.#getSelectedOptions(firstIndex);
    const second = this.#getSelectedOptions(secondIndex);
    return first.length === second.length && first.every((value, index) => value === second[index]);
  }

  #ensureValidCombination(itemIndex, changedOptionIndex, changedValue) {
    const exact = this.#findVariant(itemIndex);
    if (exact?.available) return;

    // Never silently swap a customer's size/colour to make an invalid
    // combination purchasable. This matters even when inventory tracking is
    // disabled: a product can still have combinations that do not exist.
    // Keep the shopper's explicit choice and let availability + validation
    // explain the conflict instead of adding a different variant to the bag.
    const changedControl = this.#getOptionControls(itemIndex).find(
      (control) => Number(control.dataset.optionIndex) === Number(changedOptionIndex)
    );
    if (changedControl instanceof HTMLSelectElement && changedControl.value !== changedValue) {
      changedControl.value = changedValue;
    }
  }

  #applyMasterOption(input) {
    const optionIndex = Number(input.dataset.optionIndex || 0);
    const value = input.value;

    const itemIndices = this.#isCompleteSet() ? [1, 2] : [1, 2, 3];
    for (const itemIndex of itemIndices) {
      const control = this.#getOptionControls(itemIndex).find(
        (candidate) => Number(candidate.dataset.optionIndex) === optionIndex
      );
      if (!(control instanceof HTMLSelectElement)) continue;
      control.value = value;
      this.#ensureValidCombination(itemIndex, optionIndex, value);
    }

    if (!this.#isCompleteSet()) this.#copySelections(2, 3);
  }

  #normaliseColourName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/\b(colou?r|kleur)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  #parseRgb(value) {
    if (!value) return null;
    const numbers = String(value).match(/-?\d+(?:\.\d+)?/g)?.slice(0, 3).map(Number);
    if (!numbers || numbers.length !== 3 || numbers.some((number) => !Number.isFinite(number))) return null;
    return numbers.map((number) => Math.max(0, Math.min(255, Math.round(number))));
  }

  #mixWith(rgb, mix, amount) {
    return rgb.map((channel, index) => Math.round(channel + (mix[index] - channel) * amount));
  }

  #colourDescriptor(option) {
    const rawName = option?.value || option?.textContent || '';
    const name = this.#normaliseColourName(rawName);
    const compactName = name.replace(/\s+/g, '');
    const words = name.split(' ').filter(Boolean);
    const canonicalWords = words.map((word) => AURELIA_COLOUR_ALIASES[word] || AURELIA_COLOUR_ALIASES[word.replace(/\s+/g, '')] || word);

    let key = null;
    for (const [phrase, phraseKey] of AURELIA_COLOUR_PHRASES) {
      if (name.includes(phrase)) {
        key = phraseKey;
        break;
      }
    }

    if (!key && AURELIA_COLOUR_ALIASES[compactName]) key = AURELIA_COLOUR_ALIASES[compactName];
    if (!key) {
      key = canonicalWords.find((word) => Object.prototype.hasOwnProperty.call(AURELIA_COLOUR_LIBRARY, word)) || null;
    }

    const savedSwatchRgb = this.#parseRgb(option?.dataset?.swatchRgb);
    let rgb = savedSwatchRgb;
    if (!rgb && key) rgb = [...AURELIA_COLOUR_LIBRARY[key]];

    if (rgb && !savedSwatchRgb) {
      const hasLightModifier = words.some((word) => ['baby', 'light', 'pale', 'pastel', 'powder', 'soft'].includes(word));
      const hasDarkModifier = words.some((word) => ['dark', 'deep'].includes(word));
      const hasMutedModifier = words.some((word) => ['dusty', 'muted', 'washed'].includes(word));

      if (hasLightModifier && !['sky', 'mint', 'blush', 'lavender', 'lilac', 'offwhite'].includes(key)) {
        rgb = this.#mixWith(rgb, [255, 255, 255], 0.34);
      }
      if (hasDarkModifier && !['navy', 'midnight', 'charcoal', 'forest', 'burgundy', 'chocolate', 'plum'].includes(key)) {
        rgb = this.#mixWith(rgb, [0, 0, 0], 0.28);
      }
      if (hasMutedModifier) rgb = this.#mixWith(rgb, [145, 140, 135], 0.18);
    }

    const canonical = canonicalWords
      .filter((word) => !['and', 'colour', 'color', 'plain', 'solid'].includes(word))
      .join(' ');
    const pattern = Array.from(AURELIA_PATTERN_WORDS).some((word) => name.includes(word));

    return { name, canonical, key, rgb, pattern };
  }

  #rgbToLab(rgb) {
    const [red, green, blue] = rgb.map((channel) => {
      const value = channel / 255;
      return value > 0.04045 ? ((value + 0.055) / 1.055) ** 2.4 : value / 12.92;
    });

    const x = (red * 0.4124 + green * 0.3576 + blue * 0.1805) / 0.95047;
    const y = red * 0.2126 + green * 0.7152 + blue * 0.0722;
    const z = (red * 0.0193 + green * 0.1192 + blue * 0.9505) / 1.08883;
    const transform = (value) => value > 0.008856 ? value ** (1 / 3) : (7.787 * value) + (16 / 116);
    const fx = transform(x);
    const fy = transform(y);
    const fz = transform(z);

    return [(116 * fy) - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  #colourDistance(first, second) {
    if (first.name && first.name === second.name) return -1000;
    if (first.canonical && first.canonical === second.canonical) return -850;

    let score = 120;
    if (first.rgb && second.rgb) {
      const firstLab = this.#rgbToLab(first.rgb);
      const secondLab = this.#rgbToLab(second.rgb);
      score = Math.sqrt(firstLab.reduce((total, value, index) => total + ((value - secondLab[index]) ** 2), 0));
    }

    if (first.key && second.key && first.key === second.key) score -= 10;
    if (second.pattern && !first.pattern) score += 14;

    const firstTokens = new Set(first.canonical.split(' ').filter(Boolean));
    const secondTokens = new Set(second.canonical.split(' ').filter(Boolean));
    const commonTokens = Array.from(firstTokens).filter((token) => secondTokens.has(token)).length;
    score -= commonTokens * 8;

    return score;
  }

  #colourOptionHasAvailableVariant(itemIndex, control, optionValue) {
    const colourIndex = Number(control.dataset.optionIndex || 0);
    return this.#variantsForItem(itemIndex).some(
      (variant) => variant.available && Array.isArray(variant.options) && variant.options[colourIndex] === optionValue
    );
  }

  #colourOptionKeepsCurrentSelections(itemIndex, control, optionValue) {
    const colourIndex = Number(control.dataset.optionIndex || 0);
    const selected = this.#getSelectedOptions(itemIndex);
    return this.#variantsForItem(itemIndex).some((variant) => {
      if (!variant.available || !Array.isArray(variant.options) || variant.options[colourIndex] !== optionValue) return false;
      return selected.every((value, index) => index === colourIndex || !value || variant.options[index] === value);
    });
  }

  #matchSetColourToMain() {
    if (!this.#isCompleteSet()) return;

    const mainColour = this.#getOptionControls(2).find((control) => control.dataset.optionType === 'colour');
    const setColour = this.#getOptionControls(3).find((control) => control.dataset.optionType === 'colour');
    if (!(mainColour instanceof HTMLSelectElement) || !(setColour instanceof HTMLSelectElement)) return;

    const mainOption = mainColour.selectedOptions[0];
    if (!(mainOption instanceof HTMLOptionElement)) return;

    const availableOptions = Array.from(setColour.options).filter((option) =>
      this.#colourOptionHasAvailableVariant(3, setColour, option.value)
    );
    if (!availableOptions.length) return;

    const optionsKeepingCurrentSelections = availableOptions.filter((option) =>
      this.#colourOptionKeepsCurrentSelections(3, setColour, option.value)
    );
    const candidateOptions = optionsKeepingCurrentSelections.length
      ? optionsKeepingCurrentSelections
      : availableOptions;

    const target = this.#colourDescriptor(mainOption);
    const ranked = candidateOptions
      .map((option, order) => {
        const descriptor = this.#colourDescriptor(option);
        const score = this.#colourDistance(target, descriptor);
        return { option, score, order };
      })
      .sort((first, second) => first.score - second.score || first.order - second.order);

    const bestMatch = ranked[0]?.option;
    if (!(bestMatch instanceof HTMLOptionElement)) return;

    setColour.value = bestMatch.value;
    this.#ensureValidCombination(3, Number(setColour.dataset.optionIndex || 0), bestMatch.value);
  }

  #discountedPrice(price) {
    const percentage = Math.max(0, Math.min(100, Number(this.dataset.discountPercent || 0)));
    return Math.round((Number(price) * (100 - percentage)) / 100);
  }

  #format(cents) {
    return formatMoney(
      Number(cents) || 0,
      this.dataset.moneyFormat || '${{amount}}',
      this.dataset.currency || 'AUD'
    );
  }

  #formatFreeShippingAmount(cents) {
    const amount = formatMoney(
      Number(cents) || 0,
      '{{amount}}',
      this.dataset.currency || 'AUD'
    );

    switch (this.dataset.marketCountry) {
      case 'AU':
        return `A$${amount}`;
      case 'NZ':
        return `NZ$${amount}`;
      case 'US':
        return `US$${amount}`;
      case 'GB':
        return `£${amount}`;
      case 'SG':
        return `S$${amount}`;
      default:
        return this.#format(cents);
    }
  }

  #update() {
    const quantity = this.#getOfferQuantity();
    this.dataset.offerQuantity = String(quantity);

    this.#updateOfferCards(quantity);
    [1, 2, 3].forEach((itemIndex) => {
      this.#updateOptionAvailability(itemIndex);
      this.#updateItemSummary(itemIndex);
      this.#updateItemImage(itemIndex);
    });
    this.#updateMasterPicker();

    const singleVariant = this.#findVariant(1);
    const bundleVariantOne = this.#findVariant(2);
    const bundleVariantTwo = this.#findVariant(3);
    const singleDisplay = this.#findDisplayVariant(1);
    const bundleDisplayOne = this.#findDisplayVariant(2);
    const bundleDisplayTwo = this.#findDisplayVariant(3);

    this.#setItemStatus(1, singleVariant, quantity === 1);
    this.#setItemStatus(2, bundleVariantOne, quantity === 2);
    this.#setItemStatus(3, bundleVariantTwo, quantity === 2);

    const fallbackPrice = Number(this.dataset.initialPrice || 0);
    const singlePrice = Number(singleDisplay?.price || fallbackPrice);
    const bundlePriceOne = Number(bundleDisplayOne?.price || fallbackPrice);
    const bundlePriceTwo = Number(bundleDisplayTwo?.price || fallbackPrice);
    const regularBundleTotal = bundlePriceOne + bundlePriceTwo;
    const saleBundleTotal = this.#discountedPrice(bundlePriceOne) + this.#discountedPrice(bundlePriceTwo);

    this.#setText('[data-offer-one-price]', this.#format(singlePrice));
    this.#setText('[data-offer-two-compare]', this.#format(regularBundleTotal));
    this.#setText('[data-offer-two-price]', this.#format(this.#isCompleteSet() ? saleBundleTotal : Math.round(saleBundleTotal / 2)));

    this.#setText('[data-item-original-price="2"]', this.#format(bundlePriceOne));
    this.#setText('[data-item-sale-price="2"]', this.#format(this.#discountedPrice(bundlePriceOne)));
    this.#setText('[data-item-original-price="3"]', this.#format(bundlePriceTwo));
    this.#setText('[data-item-sale-price="3"]', this.#format(this.#discountedPrice(bundlePriceTwo)));

    const itemsEqual = !this.#isCompleteSet() && this.#areItemsEqual(2, 3);
    const copyButton = this.querySelector('[data-copy-item-one]');
    const matchStatus = this.querySelector('[data-match-status]');
    if (copyButton instanceof HTMLButtonElement) copyButton.hidden = quantity !== 2 || itemsEqual;
    if (matchStatus instanceof HTMLElement) matchStatus.hidden = quantity !== 2 || !itemsEqual;

    const selectedTotal = quantity === 2 ? saleBundleTotal : singlePrice;
    this.#updateShippingStatus(selectedTotal);

    if (!this.#isLoading && !this.hasAttribute('data-success-state')) {
      this.#setText(
        '[data-button-label]',
        quantity === 2 ? this.dataset.buttonTwoLabel || 'Add 2 to bag' : this.dataset.buttonOneLabel || 'Add to bag'
      );
      this.#setText('[data-button-price]', this.#format(selectedTotal));
    }

    const stickyTitle = quantity === 2 && this.#isCompleteSet()
      ? `${this.dataset.productTitle || ''} + ${this.dataset.secondProductTitle || ''}`
      : this.dataset.productTitle || '';
    this.#setText('[data-sticky-title]', stickyTitle);
    this.#setText('[data-sticky-count]', quantity === 2 ? (this.#isCompleteSet() ? '2-piece set' : '2 items') : '1 item');
    this.#setText('[data-sticky-price]', this.#format(selectedTotal));
    this.#setText('[data-sticky-selection]', this.#getStickySelectionSummary(quantity));

    const stickyImageVariant = quantity === 2 ? bundleDisplayOne : singleDisplay;
    const stickyImage = this.#getStickyBar()?.querySelector('[data-sticky-image]');
    if (stickyImage instanceof HTMLImageElement && stickyImageVariant?.image && stickyImage.src !== stickyImageVariant.image) {
      stickyImage.src = stickyImageVariant.image;
    }

    const stickyButton = this.#getStickyBar()?.querySelector('[data-sticky-submit]');
    if (!this.#isLoading && !this.hasAttribute('data-success-state')) {
      this.#setText(
        '[data-sticky-button-desktop]',
        quantity === 2 ? this.dataset.buttonTwoLabel || 'Add 2 to bag' : this.dataset.buttonOneLabel || 'Add to bag'
      );
      this.#setText('[data-sticky-button-mobile]', this.dataset.stickyButtonLabel || 'Add to bag');
    }

    const validation = this.#validateSelections();
    const hasAvailableVariant = this.#getActiveItemIndices().every((itemIndex) => this.#variantsForItem(itemIndex).some((variant) => variant.available));
    const canSubmit = hasAvailableVariant && validation.valid;
    const submit = this.querySelector('[data-bundle-submit]');
    if (submit instanceof HTMLButtonElement) submit.disabled = this.#isLoading || !hasAvailableVariant;
    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.disabled = this.#isLoading || !hasAvailableVariant;
      stickyButton.dataset.selectionValid = String(canSubmit);
      stickyButton.setAttribute('aria-label', canSubmit
        ? `${quantity === 2 ? this.dataset.buttonTwoLabel || 'Add 2 to bag' : this.dataset.buttonOneLabel || 'Add to bag'} ${this.#format(selectedTotal)}`
        : validation.message || this.dataset.unavailableLabel || 'Choose available options'
      );
    }

    this.#updateStickyVisibility();
  }

  #updateOfferCards(quantity) {
    for (const card of this.querySelectorAll('[data-offer-card]')) {
      const cardQuantity = Number(card.getAttribute('data-offer-card'));
      const selected = cardQuantity === quantity;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-selected', String(selected));
      const body = card.querySelector('[data-offer-body]');
      if (body instanceof HTMLElement) body.hidden = !selected;
    }
  }

  #updateOptionAvailability(itemIndex) {
    const variants = this.#variantsForItem(itemIndex);
    const controls = this.#getOptionControls(itemIndex);
    const selected = controls.map((control) => control.value);

    for (const control of controls) {
      const optionIndex = Number(control.dataset.optionIndex);
      for (const option of Array.from(control.options)) {
        const available = variants.some((variant) => {
          if (!variant.available || !Array.isArray(variant.options)) return false;
          if (variant.options[optionIndex] !== option.value) return false;
          return selected.every((value, index) => index === optionIndex || !value || variant.options[index] === value);
        });
        option.disabled = !available;
      }
    }
  }

  #updateItemSummary(itemIndex) {
    const summary = this.querySelector(`[data-item-summary="${itemIndex}"]`);
    if (!(summary instanceof HTMLElement)) return;
    summary.textContent = this.#getSelectedDisplayOptions(itemIndex).filter(Boolean).join(' · ');
  }

  #getStickySelectionSummary(quantity) {
    const formatItem = (itemIndex) => this.#getSelectedDisplayOptions(itemIndex).filter(Boolean).join(' / ');
    if (quantity === 2) {
      const first = formatItem(2);
      const second = formatItem(3);
      return [first, second].filter(Boolean).join(' + ');
    }
    return formatItem(1);
  }

  #updateItemImage(itemIndex) {
    const variant = this.#findDisplayVariant(itemIndex);
    const image = this.querySelector(`[data-item-image="${itemIndex}"]`);
    if (!(image instanceof HTMLImageElement) || !variant?.image) return;
    if (image.src !== variant.image) image.src = variant.image;
  }

  #updateMasterPicker() {
    const primaryIndex = this.#getPrimaryItemIndex();
    const primaryControls = this.#getOptionControls(primaryIndex);

    for (const group of this.querySelectorAll('[data-master-group]')) {
      if (!(group instanceof HTMLFieldSetElement)) continue;
      const optionIndex = Number(group.dataset.optionIndex || 0);
      const control = primaryControls.find((candidate) => Number(candidate.dataset.optionIndex) === optionIndex);
      if (!(control instanceof HTMLSelectElement)) continue;

      for (const input of group.querySelectorAll('[data-master-option]')) {
        if (input instanceof HTMLInputElement) input.checked = input.value === control.value;
      }

      const label = group.querySelector(`[data-master-selected-label="${optionIndex}"]`);
      if (label instanceof HTMLElement) label.textContent = control.value;
    }
  }

  #setItemStatus(itemIndex, variant, active) {
    const status = this.querySelector(`[data-item-status="${itemIndex}"]`);
    if (!(status instanceof HTMLElement)) return;
    status.textContent = active && variant && !variant.available
      ? this.dataset.unavailableLabel || 'This product is currently unavailable.'
      : '';
  }

  #updateShippingStatus(total) {
    const threshold = Number(this.dataset.freeShippingThreshold || 0);
    const status = this.querySelector('[data-shipping-status]');
    const progressBar = this.querySelector('[data-shipping-progress-bar]');
    const shippingChip = this.querySelector('[data-shipping-chip]');
    const stickyShipping = this.#getStickyBar()?.querySelector('[data-sticky-shipping]');

    if (threshold <= 0) {
      const unlockedLabel = this.dataset.freeShippingUnlockedLabel || 'Free shipping unlocked';
      if (status instanceof HTMLElement) status.textContent = unlockedLabel;
      if (stickyShipping instanceof HTMLElement) stickyShipping.textContent = unlockedLabel;
      if (progressBar instanceof HTMLElement) progressBar.style.width = '100%';
      if (shippingChip instanceof HTMLElement) shippingChip.hidden = false;
      return;
    }

    const unlocked = total >= threshold;
    const remaining = Math.max(0, threshold - total);
    const progress = Math.max(0, Math.min(100, (total / threshold) * 100));
    const template = unlocked
      ? this.dataset.freeShippingUnlockedLabel || 'Free shipping unlocked'
      : this.dataset.freeShippingProgressLabel || 'Add {amount} more for free shipping';
    const message = template.replace('{amount}', this.#formatFreeShippingAmount(remaining));

    if (status instanceof HTMLElement) status.textContent = message;
    if (stickyShipping instanceof HTMLElement) stickyShipping.textContent = message;
    if (progressBar instanceof HTMLElement) progressBar.style.width = `${progress}%`;
    if (shippingChip instanceof HTMLElement) shippingChip.hidden = !unlocked;
  }

  #validateSelections() {
    for (const itemIndex of this.#getActiveItemIndices()) {
      const variant = this.#findVariant(itemIndex);
      if (variant?.available) continue;

      const item = this.querySelector(`[data-bundle-item="${itemIndex}"]`);
      const position = item instanceof HTMLElement ? item.dataset.itemPosition || '' : '';
      const product = item instanceof HTMLElement
        ? item.dataset.itemProductTitle || this.#productTitleForItem(itemIndex)
        : this.#productTitleForItem(itemIndex);
      const template = this.dataset.optionUnavailableLabel || 'The selected options for your {position} {product} are unavailable.';
      const message = position === 'single'
        ? `The selected options for ${product} are unavailable.`
        : template.replaceAll('{position}', position).replaceAll('{product}', product);
      return { valid: false, message, target: item };
    }

    return { valid: true, message: '', target: null };
  }

  #focusValidationTarget(target) {
    if (!(target instanceof HTMLElement)) return;
    target.classList.add('has-error');
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      const focusable = target.querySelector('select:not(:disabled), input:not(:disabled), button:not(:disabled)');
      if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    }, 300);
  }

  #clearValidationErrors() {
    for (const element of this.querySelectorAll('.has-error')) element.classList.remove('has-error');
  }

  #getStickyBar() {
    return this.#stickyBar || this.querySelector('[data-sticky-bar]');
  }

  #setText(selector, value) {
    const roots = [this];
    const sticky = this.#getStickyBar();
    if (sticky && !this.contains(sticky)) roots.push(sticky);

    for (const root of roots) {
      for (const element of root.querySelectorAll(selector)) {
        element.textContent = value;
      }
    }
  }

  #clearMessage() {
    const message = this.querySelector('[data-bundle-message]');
    if (!(message instanceof HTMLElement)) return;
    message.textContent = '';
    message.removeAttribute('data-success');
  }

  #showMessage(text, success = false) {
    const message = this.querySelector('[data-bundle-message]');
    if (!(message instanceof HTMLElement)) return;
    message.textContent = text;
    if (success) message.dataset.success = 'true';
    else message.removeAttribute('data-success');
  }

  #setLoading(isLoading) {
    this.#isLoading = isLoading;
    this.setAttribute('aria-busy', String(isLoading));
    this.classList.toggle('is-loading', isLoading);

    const button = this.querySelector('[data-bundle-submit]');
    const stickyButton = this.#getStickyBar()?.querySelector('[data-sticky-submit]');
    if (button instanceof HTMLButtonElement) button.disabled = isLoading;
    if (stickyButton instanceof HTMLButtonElement) stickyButton.disabled = isLoading;

    if (isLoading) {
      const loadingLabel = this.dataset.addingLabel || 'Adding to bag…';
      this.#setText('[data-button-label]', loadingLabel);
      this.#setText('[data-sticky-button-desktop]', loadingLabel);
      this.#setText('[data-sticky-button-mobile]', loadingLabel);
    }
  }

  #setSuccessState() {
    this.setAttribute('data-success-state', 'true');
    const successLabel = `✓ ${this.dataset.addedLabel || 'Added to bag'}`;
    this.#setText('[data-button-label]', successLabel);
    this.#setText('[data-sticky-button-desktop]', successLabel);
    this.#setText('[data-sticky-button-mobile]', `✓ ${this.dataset.addedLabel || 'Added'}`);

    if (this.#successTimer) window.clearTimeout(this.#successTimer);
    this.#successTimer = window.setTimeout(() => {
      this.removeAttribute('data-success-state');
      this.#update();
    }, 1400);
  }

  #createLines() {
    const variants = this.#getActiveItemIndices().map((itemIndex) => this.#findVariant(itemIndex));
    if (variants.some((variant) => !variant?.available)) return [];

    const rawLines = variants.map((variant) => ({ id: Number(variant.id), quantity: 1 }));

    const grouped = new Map();
    for (const line of rawLines) grouped.set(line.id, (grouped.get(line.id) || 0) + line.quantity);
    return Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
  }

  async #fetchCartData() {
    const cartComponent = document.querySelector('cart-items-component');
    if (cartComponent) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartComponent.fetchCartData === 'function') return cartComponent.fetchCartData();
    }

    const root = window.Shopify?.routes?.root || '/';
    const cartPath = window.Theme?.routes?.cart_url || `${root}cart`;
    const response = await fetch(`${cartPath}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #handleSubmit = async () => {
    if (this.#isLoading) return;

    const validation = this.#validateSelections();
    if (!validation.valid) {
      this.#showMessage(validation.message);
      this.#focusValidationTarget(validation.target);
      return;
    }

    const lines = this.#createLines();
    if (!lines.length) {
      this.#showMessage(this.dataset.unavailableLabel || 'This product is currently unavailable.');
      return;
    }

    this.#setLoading(true);
    this.#clearMessage();

    const cartComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = cartComponents
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    }));

    let succeeded = false;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorLabel || 'Add to cart failed.');
      }

      const cart = await this.#fetchCartData();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aurelia-bundle-buy-box',
          sourceId: this.dataset.productId || '',
          itemCount: lines.reduce((total, line) => total + line.quantity, 0),
          productId: this.dataset.productId || '',
          sections: result.sections,
          didError: false,
        },
      });

      succeeded = true;
      this.#showMessage(this.dataset.addedLabel || 'Added to bag', true);
      this.#setSuccessState();
    } catch (error) {
      const text = error instanceof Error ? error.message : this.dataset.errorLabel || 'Something went wrong.';
      this.dispatchEvent(new CartErrorEvent({ error: text, code: 'INVALID' }));
      deferred.resolve({
        cart: null,
        userErrors: [{ message: text }],
        detail: { didError: true, source: 'aurelia-bundle-buy-box', productId: this.dataset.productId || '' },
      });
      this.#showMessage(text || this.dataset.errorLabel || 'Something went wrong. Please try again.');
    } finally {
      this.#setLoading(false);
      if (!succeeded) this.#update();
    }
  };

  async #syncPrimaryVariant() {
    const itemIndex = this.#getPrimaryItemIndex();
    const variant = this.#findVariant(itemIndex);
    const selectedOptions = this.#getSelectedOptionDetails(itemIndex);
    if (!variant?.available) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferred = ProductSelectEvent.createPromise();
    this.dispatchEvent(new ProductSelectEvent({
      product: {
        id: String(this.dataset.productId || ''),
        title: this.dataset.productTitle || '',
        handle: this.dataset.productHandle || '',
      },
      selectedOptions: selectedOptions.map(({ name, value }) => ({ name, value })),
      detail: { variantId: String(variant.id) },
      promise: deferred.promise,
    }));

    try {
      const separator = (this.dataset.productUrl || '').includes('?') ? '&' : '?';
      const requestUrl = `${this.dataset.productUrl}${separator}variant=${encodeURIComponent(String(variant.id))}`;
      const response = await fetch(requestUrl, {
        signal: this.#abortController.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Unable to update selected variant.');

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const currentVariantScript = html.querySelector(
        `aurelia-bundle-buy-box[data-product-id="${this.dataset.productId}"] [data-current-variant]`
      );
      const resource = currentVariantScript?.textContent ? JSON.parse(currentVariantScript.textContent) : null;

      deferred.resolve({
        variant: resource,
        detail: {
          html,
          productId: String(this.dataset.productId || ''),
          sourceId: String(variant.id),
          resource,
        },
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      deferred.reject(error);
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[Aurelia bundle] Variant preview update failed.', error);
    }
  }

  #copyStickyThemeVariables(sticky) {
    const computed = window.getComputedStyle(this);
    const customProperties = [
      '--as-bundle-card',
      '--as-bundle-selected',
      '--as-bundle-text',
      '--as-bundle-muted',
      '--as-bundle-border',
      '--as-bundle-accent',
      '--as-bundle-accent-soft',
      '--as-bundle-button',
      '--as-bundle-button-text',
      '--as-bundle-error',
    ];

    for (const property of customProperties) {
      const value = computed.getPropertyValue(property).trim();
      if (value) sticky.style.setProperty(property, value);
    }

    sticky.style.fontFamily = computed.fontFamily;
    sticky.style.fontSize = computed.fontSize;
  }

  #setupStickyBar() {
    if (this.dataset.stickyEnabled !== 'true') return;

    const button = this.querySelector('[data-bundle-submit]');
    const sticky = this.querySelector('[data-sticky-bar]');
    if (!(button instanceof HTMLElement) || !(sticky instanceof HTMLElement)) return;

    this.#stickyBar = sticky;
    this.#productInformationSection =
      this.closest('[id^="shopify-section-"]') ||
      this.closest('.shopify-section') ||
      this.closest('section') ||
      this;

    /*
      Portal the sticky bar to <body>. Horizon product media/details can create
      their own stacking and containing contexts. Moving the fixed bar to body
      guarantees it cannot sit behind product content or be clipped by it.
    */
    this.#stickyOriginalParent = sticky.parentNode;
    this.#stickyOriginalNextSibling = sticky.nextSibling;
    this.#copyStickyThemeVariables(sticky);
    sticky.addEventListener('click', this.#handleClick);
    document.body.appendChild(sticky);

    if (typeof IntersectionObserver !== 'undefined') {
      this.#componentObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#componentHasEnteredViewport = true;
        this.#updateStickyVisibility();
      }, { threshold: 0.05 });
      this.#componentObserver.observe(this);

      this.#buttonObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#buttonHasEnteredViewport = true;
        this.#updateStickyVisibility();
      }, { threshold: 0.15 });
      this.#buttonObserver.observe(button);
    }

    if (typeof MutationObserver !== 'undefined' && document.body) {
      this.#overlayObserver = new MutationObserver(() => this.#updateStickyVisibility());
      this.#overlayObserver.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'aria-hidden'],
      });
    }
  }

  #isOverlayOpen() {
    if (document.querySelector('dialog[open]')) return true;

    const overlays = document.querySelectorAll(
      'cart-drawer, .cart-drawer, [data-cart-drawer], menu-drawer, .menu-drawer, [data-drawer]'
    );

    return Array.from(overlays).some((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('open')) return true;
      if (element.getAttribute('aria-hidden') === 'false') return true;
      if (element.classList.contains('is-open') || element.classList.contains('active') || element.classList.contains('open')) {
        return true;
      }
      return Boolean(element.querySelector('dialog[open], [aria-modal="true"][aria-hidden="false"]'));
    });
  }

  #isStickyEnabledForViewport() {
    if (window.innerWidth <= 749) return this.dataset.stickyMobileEnabled !== 'false';
    return this.dataset.stickyDesktopEnabled !== 'false';
  }

  #setStickyVisibility(visible) {
    const sticky = this.#getStickyBar();
    if (!(sticky instanceof HTMLElement)) return;

    if (sticky.hidden === visible) sticky.hidden = !visible;
    sticky.classList.toggle('is-visible', visible);
    const ariaHidden = String(!visible);
    if (sticky.getAttribute('aria-hidden') !== ariaHidden) sticky.setAttribute('aria-hidden', ariaHidden);
  }

  #updateStickyVisibility = () => {
    const sticky = this.#getStickyBar();
    const productSection = this.#productInformationSection;
    if (!(sticky instanceof HTMLElement) || !(productSection instanceof HTMLElement)) return;

    if (
      this.dataset.stickyEnabled !== 'true' ||
      !this.#isStickyEnabledForViewport() ||
      Boolean(window.Shopify?.designMode) ||
      this.#isOverlayOpen()
    ) {
      this.#setStickyVisibility(false);
      return;
    }

    const sectionRect = productSection.getBoundingClientRect();
    const pageIsVisible = document.visibilityState !== 'hidden';

    /*
      Never show over Product Information. The bar appears only after the
      complete section has moved above the viewport, and disappears immediately
      when the customer scrolls back into any part of that section.
    */
    const productInformationHasPassed = sectionRect.bottom <= 0;
    this.#setStickyVisibility(pageIsVisible && productInformationHasPassed);
  };
}

if (!customElements.get('aurelia-bundle-buy-box')) {
  customElements.define('aurelia-bundle-buy-box', AureliaBundleBuyBox);
}
